/**
 * skip-reasons — por que um nó não rodou (aba Filtros / cancelamento),
 * compartilhado entre o canvas (`BaseNode`), a sidebar de histórico
 * (`ExecutionHistorySidebar`) e o painel de teste ao vivo (`WorkflowTestPanel`).
 *
 * As duas strings de motivo são as mesmas de `graph-walker.ts` no backend
 * (`MOTIVO_FILTRO = 'filtro_nao_atendido'`, `MOTIVO_CANCELADO = 'execucao_cancelada'`);
 * não existe constante compartilhada em `@troia-v3/types` para elas ainda (Fase 4).
 */
import type { NodeRunEntry } from '@troia-v3/types';

/** Título longo, usado no tooltip do selo "Não rodou" no canvas (`BaseNode`). */
export const SKIPPED_REASON_TITLES: Record<string, string> = {
  filtro_nao_atendido: 'Não rodou: filtro não atendido — o fluxo encerrou aqui',
  execucao_cancelada: 'Não rodou: execução cancelada',
};

const SHORT: Record<string, string> = {
  filtro_nao_atendido: 'filtro não atendido',
  execucao_cancelada: 'execução cancelada',
};

/** Descrição curta do motivo, para uso em linhas de texto (sidebar, painel de teste). */
export function describeSkipReason(reason?: string): string {
  return (reason !== undefined ? SHORT[reason] : undefined) ?? 'não rodou';
}

/** Extrai `output.reason` de uma entrada de execução, se for string. */
export function reasonOf(entry: NodeRunEntry | undefined): string | undefined {
  const r = entry?.output?.['reason'];
  return typeof r === 'string' ? r : undefined;
}

export interface SkippedSummary {
  filtro: number;
  cancelado: number;
  outros: number;
}

/**
 * Conta os nós pulados por motivo, olhando QUALQUER entrada pulada de cada
 * nó em `runData` (classificado pelo motivo da PRIMEIRA entrada pulada).
 *
 * Antes (Menor (a) da revisão final da Fase 4, 16/09/2026) olhava só a
 * ÚLTIMA entrada — correto fora de loop (1 entrada por nó), mas com o
 * `control_loop` rodando de verdade um nó do corpo tem uma entrada POR
 * ITERAÇÃO: um loop `a=completa, b=pulada por filtro, c=completa` termina
 * com a última entrada `completed` e o resumo ficava mudo sobre a iteração
 * `b` que não rodou. Contar por "alguma entrada pulada" cobre os dois casos
 * (fora de loop e dentro dele) sem custo extra.
 */
export function summarizeSkippedNodes(runData: Record<string, NodeRunEntry[]> | undefined): SkippedSummary {
  const out: SkippedSummary = { filtro: 0, cancelado: 0, outros: 0 };
  if (!runData) return out;
  for (const entries of Object.values(runData)) {
    const puladas = entries.filter((e) => e?.status === 'skipped');
    if (puladas.length === 0) continue;
    const r = reasonOf(puladas[0]);
    if (r === 'filtro_nao_atendido') out.filtro += 1;
    else if (r === 'execucao_cancelada') out.cancelado += 1;
    else out.outros += 1;
  }
  return out;
}

/** Resumo em pt-BR do total de nós pulados, ou `null` quando nenhum foi pulado. */
export function describeSkippedSummary(summary: SkippedSummary): string | null {
  const total = summary.filtro + summary.cancelado + summary.outros;
  if (total === 0) return null;
  const partes: string[] = [];
  if (summary.filtro > 0) partes.push(`filtro (${summary.filtro})`);
  if (summary.cancelado > 0) partes.push(`cancelamento (${summary.cancelado})`);
  if (summary.outros > 0) partes.push(`outros (${summary.outros})`);
  return `${total} ${total === 1 ? 'nó não rodou' : 'nós não rodaram'}: ${partes.join(' · ')}`;
}
